import { useMemo } from "react";
import { useTasks } from "@/hooks/useTasks";
import type { Task } from "@/hooks/useTasks";

// ── Types ──────────────────────────────────────────────────────────────────

export interface CriticalAlert {
  task: Task;
  minutesUntil: number;
}

export interface UseCriticalAlertsResult {
  urgentTasks: CriticalAlert[];
  hasUrgentTasks: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const URGENT_THRESHOLD_MINUTES = 120;

/**
 * Parses a "HH:MM" time_label relative to today and returns the number of
 * minutes from now until that time. Returns null if the format is invalid
 * or the time has already passed.
 */
function minutesUntilTimeLabel(timeLabel: string): number | null {
  const match = timeLabel.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours > 23 || minutes > 59) return null;

  const now = new Date();
  const target = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes,
    0,
    0
  );

  const diff = (target.getTime() - now.getTime()) / 60_000;
  return diff > 0 ? diff : null; // null if already passed
}

// ── Hook ──────────────────────────────────────────────────────────────────

export function useCriticalAlerts(): UseCriticalAlertsResult {
  const { tasks } = useTasks();

  const urgentTasks = useMemo<CriticalAlert[]>(() => {
    return tasks
      .filter(
        (t) =>
          t.priority === "Critical" &&
          !t.done &&
          t.time_label !== null &&
          t.time_label !== ""
      )
      .reduce<CriticalAlert[]>((acc, task) => {
        const mins = minutesUntilTimeLabel(task.time_label!);
        if (mins !== null && mins <= URGENT_THRESHOLD_MINUTES) {
          acc.push({ task, minutesUntil: Math.floor(mins) });
        }
        return acc;
      }, [])
      .sort((a, b) => a.minutesUntil - b.minutesUntil);
  }, [tasks]);

  return {
    urgentTasks,
    hasUrgentTasks: urgentTasks.length > 0,
  };
}
